<script lang="ts" setup>
import { defineAsyncComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { ArrowRightIcon, BookOpenIcon, BuildingOffice2Icon, LifebuoyIcon, WrenchScrewdriverIcon } from '@heroicons/vue/24/outline';
import SectionHeader from '@/components/SectionHeader.vue';

const SearchLeadModal = defineAsyncComponent(() => import('@/components/SearchLeadModal.vue'));

const { t } = useI18n();

const isModalOpen = ref(false);
const isModalMounted = ref(false);

const openModal = () => {
  isModalMounted.value = true;
  isModalOpen.value = true;
};
</script>

<template>
  <section class="mb-5">
    <SectionHeader>{{ t('views.home.pathways.header') }}</SectionHeader>
    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <RouterLink
        :to="{ name: 'search' }"
        class="group hover:border-ph-green-600 focus-visible:outline-ph-green-700 flex h-full flex-col items-center gap-2 rounded-lg border border-gray-200 bg-white p-4 text-center shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2"
      >
        <span class="bg-ph-green-700/10 group-hover:bg-ph-green-700 flex size-11 items-center justify-center rounded-full transition">
          <BuildingOffice2Icon aria-hidden="true" class="text-ph-green-700 size-6 transition group-hover:text-white" />
        </span>
        <span class="font-semibold text-gray-900">{{ t('views.home.pathways.projects.title') }}</span>
        <span class="text-sm text-gray-500">{{ t('views.home.pathways.projects.description') }}</span>
        <span class="text-ph-green-700 mt-auto inline-flex items-center gap-1.5 pt-0.5 text-sm font-semibold">
          {{ t('views.home.pathways.projects.action') }}
          <ArrowRightIcon aria-hidden="true" class="size-4 transition-transform group-hover:translate-x-1" />
        </span>
      </RouterLink>

      <RouterLink
        :to="{ name: 'builders' }"
        class="group hover:border-ph-green-600 focus-visible:outline-ph-green-700 flex h-full flex-col items-center gap-2 rounded-lg border border-gray-200 bg-white p-4 text-center shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2"
      >
        <span class="bg-ph-green-700/10 group-hover:bg-ph-green-700 flex size-11 items-center justify-center rounded-full transition">
          <WrenchScrewdriverIcon aria-hidden="true" class="text-ph-green-700 size-6 transition group-hover:text-white" />
        </span>
        <span class="font-semibold text-gray-900">{{ t('views.home.pathways.builders.title') }}</span>
        <span class="text-sm text-gray-500">{{ t('views.home.pathways.builders.description') }}</span>
        <span class="text-ph-green-700 mt-auto inline-flex items-center gap-1.5 pt-0.5 text-sm font-semibold">
          {{ t('views.home.pathways.builders.action') }}
          <ArrowRightIcon aria-hidden="true" class="size-4 transition-transform group-hover:translate-x-1" />
        </span>
      </RouterLink>

      <button
        class="group hover:border-ph-green-600 focus-visible:outline-ph-green-700 flex h-full cursor-pointer flex-col items-center gap-2 rounded-lg border border-gray-200 bg-white p-4 text-center shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2"
        type="button"
        @click="openModal"
      >
        <span class="bg-ph-green-700/10 group-hover:bg-ph-green-700 flex size-11 items-center justify-center rounded-full transition">
          <LifebuoyIcon aria-hidden="true" class="text-ph-green-700 size-6 transition group-hover:text-white" />
        </span>
        <span class="font-semibold text-gray-900">{{ t('views.home.pathways.assistance.title') }}</span>
        <span class="text-sm text-gray-500">{{ t('views.home.pathways.assistance.description') }}</span>
        <span class="text-ph-green-700 mt-auto inline-flex items-center gap-1.5 pt-0.5 text-sm font-semibold">
          {{ t('views.home.pathways.assistance.action') }}
          <ArrowRightIcon aria-hidden="true" class="size-4 transition-transform group-hover:translate-x-1" />
        </span>
      </button>

      <RouterLink
        :to="{ name: 'guides' }"
        class="group hover:border-ph-green-600 focus-visible:outline-ph-green-700 flex h-full flex-col items-center gap-2 rounded-lg border border-gray-200 bg-white p-4 text-center shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2"
      >
        <span class="bg-ph-green-700/10 group-hover:bg-ph-green-700 flex size-11 items-center justify-center rounded-full transition">
          <BookOpenIcon aria-hidden="true" class="text-ph-green-700 size-6 transition group-hover:text-white" />
        </span>
        <span class="font-semibold text-gray-900">{{ t('views.home.pathways.guides.title') }}</span>
        <span class="text-sm text-gray-500">{{ t('views.home.pathways.guides.description') }}</span>
        <span class="text-ph-green-700 mt-auto inline-flex items-center gap-1.5 pt-0.5 text-sm font-semibold">
          {{ t('views.home.pathways.guides.action') }}
          <ArrowRightIcon aria-hidden="true" class="size-4 transition-transform group-hover:translate-x-1" />
        </span>
      </RouterLink>
    </div>

    <SearchLeadModal v-if="isModalMounted" v-model:open="isModalOpen" />
  </section>
</template>

<style lang="css" scoped></style>
